import { useRef, useState, useMemo, useCallback, type ChangeEvent } from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Tabs,
  TabList,
  Tab,
  Badge,
  Text,
  Textarea,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useAccessCheck } from '@/hooks/useAccess'
import { taskFieldModuleService } from '@/lib/services/taskFieldModuleService'
import type { TaskFieldModuleCatalogItem } from '@/components/helpdesk/TicketDetail/tasks/modules/types'
import { useToastNotification } from '@/lib/utils/useToastNotification'

const basePath = '/service-catalog/task-field-modules'

type TabValue = 'custom' | 'reserved'

type ModuleForm = {
  moduleKey: string
  label: string
  kind: string
  url: string
  scope: 'task' | 'ticket'
  category: 'forms' | 'operations' | 'embeds'
  active: boolean
}

const emptyForm = (): ModuleForm => ({
  moduleKey: '',
  label: '',
  kind: 'iframe',
  url: '',
  scope: 'task',
  category: 'embeds',
  active: true,
})

function urlFromConfig(item: TaskFieldModuleCatalogItem): string {
  const cfg = item.default_config ?? {}
  return String(cfg.url ?? cfg.urlTemplate ?? '')
}

function resolvedModuleKey(key: string): string {
  const trimmed = key.trim()
  if (!trimmed) return 'custom.…'
  return trimmed.startsWith('custom.') ? trimmed : `custom.${trimmed}`
}

export default function TaskFieldModulesPage() {
  const colors = useThemeColors()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess } = useToastNotification()
  const formModal = useDisclosure()
  const deleteModal = useDisclosure()
  const access = useAccessCheck()

  const [activeTab, setActiveTab] = useState<TabValue>('custom')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalCustom, setTotalCustom] = useState(0)
  const [totalReserved, setTotalReserved] = useState(0)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingItem, setEditingItem] = useState<TaskFieldModuleCatalogItem | null>(null)
  const [deleting, setDeleting] = useState<TaskFieldModuleCatalogItem | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<ModuleForm>(emptyForm)

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access]
  )

  const reservedPermissions = useMemo(
    () => ({
      ...permissions,
      can_edit: false,
      can_delete: false,
      can_create: false,
    }),
    [permissions]
  )

  const tableParams = useMemo(
    () => ({
      search: searchQuery || undefined,
      custom_only: activeTab === 'custom' ? 1 : 0,
      per_page: 50,
    }),
    [searchQuery, activeTab]
  )

  const setSubSub = useCallback((all: number, trashed: number) => {
    if (activeTab === 'custom') {
      setTotalCustom(all)
    } else {
      setTotalReserved(all)
    }
    void trashed
  }, [activeTab])

  const onDataLoad = useCallback(
    (_data: unknown[], meta?: { total?: number }) => {
      const total = meta?.total ?? 0
      if (activeTab === 'custom') setTotalCustom(total)
      else setTotalReserved(total)
    },
    [activeTab]
  )

  const customTableOptions: DataTableOptions = useMemo(
    () => ({
      showActionsColumn: true,
      displayInModal: true,
      dataFields: {
        module_key: { name: 'Key', withSort: false, maxWidth: '180px' },
        label: { name: 'Label', withSort: false, maxWidth: '200px' },
        kind: { name: 'Kind', withSort: false, maxWidth: '120px' },
        usage_total: { name: 'Used by', withSort: false, maxWidth: '100px' },
        is_active: {
          name: 'Active',
          withSort: false,
          maxWidth: '90px',
          badge: { true: 'green', false: 'gray' },
          badgeLabels: { true: 'Yes', false: 'No' },
        },
      },
      dataSource: basePath,
      primaryKey: 'id',
      softDelete: false,
      hideSelection: true,
      noRecordText: 'No custom modules found. Click “Add Custom Module” to create one.',
      otherActions: [
        {
          name: 'Delete',
          onClick: (row: TaskFieldModuleCatalogItem) => {
            setDeleting(row)
            deleteModal.onOpen()
          },
        },
      ],
    }),
    [deleteModal]
  )

  const reservedTableOptions: DataTableOptions = useMemo(
    () => ({
      showActionsColumn: false,
      displayInModal: false,
      dataFields: {
        module_key: { name: 'Key', withSort: false, maxWidth: '180px' },
        label: { name: 'Label', withSort: false, maxWidth: '200px' },
        category: { name: 'Category', withSort: false, maxWidth: '120px' },
        scope: { name: 'Scope', withSort: false, maxWidth: '100px' },
        usage_total: { name: 'Used by', withSort: false, maxWidth: '100px' },
      },
      dataSource: basePath,
      primaryKey: 'id',
      softDelete: false,
      hideSelection: true,
      noRecordText: 'No reserved modules found',
      otherActions: [],
    }),
    []
  )

  const tableOptions = activeTab === 'custom' ? customTableOptions : reservedTableOptions

  const resetForm = () => {
    setForm(emptyForm())
    setEditingId(null)
    setEditingItem(null)
  }

  const openCreate = () => {
    resetForm()
    formModal.onOpen()
  }

  const openEdit = async (rowOrId: number | TaskFieldModuleCatalogItem) => {
    const id = typeof rowOrId === 'object' ? rowOrId.id : rowOrId
    if (!id) return

    setEditingId(Number(id))
    setEditingItem(null)
    try {
      const item = await taskFieldModuleService.get(Number(id))
      setEditingItem(item)
      setForm({
        moduleKey: item.module_key,
        label: item.label,
        kind: item.kind === 'react' ? 'react' : 'iframe',
        url: urlFromConfig(item),
        scope: (item.scope as 'task' | 'ticket') ?? 'task',
        category: (item.category as 'forms' | 'operations' | 'embeds') ?? 'embeds',
        active: item.is_active !== false,
      })
      formModal.onOpen()
    } catch (e) {
      showError(e as Error)
    }
  }

  const handleSave = async () => {
    if (!form.label.trim()) {
      showError(new Error('Label is required'))
      return
    }
    if (!editingId && !form.moduleKey.trim()) {
      showError(new Error('Module key is required'))
      return
    }

    setSaving(true)
    try {
      const defaultConfig =
        form.kind === 'iframe'
          ? { url: form.url, height: 480, ...(editingItem?.default_config?.sandbox ? { sandbox: editingItem.default_config.sandbox } : {}) }
          : {
              urlTemplate: form.url,
              buttonLabel: String(editingItem?.default_config?.buttonLabel ?? 'Open external system'),
            }

      if (editingId) {
        await taskFieldModuleService.update(editingId, {
          label: form.label.trim(),
          kind: form.kind,
          scope: form.scope,
          category: form.category,
          default_config: defaultConfig,
          is_active: form.active,
        })
        showSuccess('Success', 'Module updated successfully')
      } else {
        await taskFieldModuleService.create({
          module_key: resolvedModuleKey(form.moduleKey),
          label: form.label.trim(),
          kind: form.kind,
          category: form.category,
          scope: form.scope,
          default_config: defaultConfig,
          badge: 'Custom',
          is_active: form.active,
        })
        showSuccess('Success', 'Module created successfully')
      }

      formModal.onClose()
      resetForm()
      tableRef.current?.reload()
    } catch (e) {
      showError(e as Error)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleting?.id) return
    setSaving(true)
    try {
      await taskFieldModuleService.delete(deleting.id)
      showSuccess('Success', 'Module deleted successfully')
      deleteModal.onClose()
      setDeleting(null)
      tableRef.current?.reload()
    } catch (e) {
      showError(e as Error)
    } finally {
      setSaving(false)
    }
  }

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'custom' : 'reserved')
    setSearchQuery('')
    tableRef.current?.applyFilter('search', '')
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    const q = searchRef.current?.value ?? searchQuery
    setSearchQuery(q)
    tableRef.current?.applyFilter('search', q || '')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  return (
    <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
      <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
        <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
          <CardHeader flexShrink={0} py={3}>
            <Heading size="md" mb={1}>
              Task Field Modules
            </Heading>
            <Text fontSize="sm" color={colors.text}>
              Register custom embed modules for the task template builder. Reserved modules are read-only.
            </Text>
          </CardHeader>

          <CardHeader py={0} px={0} flexShrink={0}>
            <Box pt={2} pb={0}>
              <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                <Tabs index={activeTab === 'custom' ? 0 : 1} onChange={handleTabChange} variant="enclosed">
                  <TabList borderBottom="none">
                    <Tab>
                      Custom <Badge ml={2} colorScheme="blue">{totalCustom}</Badge>
                    </Tab>
                    <Tab>
                      Reserved <Badge ml={2} colorScheme="gray">{totalReserved}</Badge>
                    </Tab>
                  </TabList>
                </Tabs>
                {activeTab === 'custom' && (
                  <Box pb={2}>
                    <ActionButtons pathname={basePath} onCreate={openCreate} createLabel="Add Custom Module" />
                  </Box>
                )}
              </HStack>
              <Box borderBottom="2px" borderColor={colors.border} mx={4} />
            </Box>
          </CardHeader>

          <CardHeader py={1} pt={3} pb={2} flexShrink={0} px={5}>
            <HStack spacing={4} flexWrap="wrap">
              <Box flex="0 1 auto" minW="250px" maxW="400px">
                <InputGroup size="sm">
                  <Input
                    ref={searchRef}
                    placeholder="Search by key or label…"
                    value={searchQuery}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    pr="2.5rem"
                  />
                  <InputRightElement width="2.5rem">
                    <IconButton
                      aria-label="Search"
                      icon={<Search size={16} />}
                      size="sm"
                      h="1.75rem"
                      w="1.75rem"
                      onClick={handleSearch}
                      variant="ghost"
                    />
                  </InputRightElement>
                </InputGroup>
              </Box>
            </HStack>
          </CardHeader>

          <CardBody p={0} pt={1} pb={4} mx={5} flex="1" overflow="hidden" display="flex" flexDirection="column" minH={0}>
            <Box h="100%" display="flex" flexDirection="column" minH={0}>
              <DataTable
                ref={tableRef}
                options={tableOptions}
                params={tableParams}
                setSubSub={setSubSub}
                onDataLoad={onDataLoad}
                access={activeTab === 'custom' ? permissions : reservedPermissions}
                onOpenModal={openEdit}
                customRenderers={{
                  module_key: (value) => (
                    <Text as="span" fontFamily="mono" fontSize="xs">
                      {value ? String(value) : '–'}
                    </Text>
                  ),
                  scope: (value) => (
                    <Badge colorScheme={String(value) === 'ticket' ? 'purple' : 'blue'}>
                      {String(value || 'task')}
                    </Badge>
                  ),
                  usage_total: (_value, row) => (
                    <Text>{Number((row as { usage?: { total?: number } })?.usage?.total ?? 0)}</Text>
                  ),
                }}
              />
            </Box>
          </CardBody>
        </Card>
      </VStack>

      <Modal
        isOpen={formModal.isOpen}
        onClose={() => {
          formModal.onClose()
          resetForm()
        }}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingId ? 'Edit Custom Module' : 'Add Custom Module'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl isRequired={!editingId} isReadOnly={!!editingId}>
                <FormLabel>Module key</FormLabel>
                {editingId ? (
                  <Input value={form.moduleKey} isReadOnly fontFamily="mono" fontSize="sm" />
                ) : (
                  <>
                    <Input
                      value={form.moduleKey}
                      onChange={(e) => setForm((p) => ({ ...p, moduleKey: e.target.value }))}
                      placeholder="sima_v1"
                    />
                    <Text fontSize="xs" color={colors.textSecondary} mt={1}>
                      Saved as {resolvedModuleKey(form.moduleKey)}
                    </Text>
                  </>
                )}
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Label</FormLabel>
                <Input value={form.label} onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))} />
              </FormControl>
              <FormControl>
                <FormLabel>Kind</FormLabel>
                <Select value={form.kind} onChange={(e) => setForm((p) => ({ ...p, kind: e.target.value }))}>
                  <option value="iframe">iframe</option>
                  <option value="react">external link panel</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Scope</FormLabel>
                <Select value={form.scope} onChange={(e) => setForm((p) => ({ ...p, scope: e.target.value as 'task' | 'ticket' }))}>
                  <option value="task">Task</option>
                  <option value="ticket">Ticket</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Category</FormLabel>
                <Select value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value as 'forms' | 'operations' | 'embeds' }))}>
                  <option value="forms">Forms</option>
                  <option value="operations">Operations</option>
                  <option value="embeds">Embeds</option>
                </Select>
              </FormControl>
              <Text fontSize="xs" color={colors.textSecondary}>
                Ticket-scoped modules appear once per ticket; task-scoped modules appear per task instance.
              </Text>
              <FormControl>
                <FormLabel>URL template</FormLabel>
                <Textarea
                  value={form.url}
                  onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))}
                  placeholder="https://example.com/form?ticket={{ticket_id}}"
                  rows={2}
                />
              </FormControl>
              <Checkbox isChecked={form.active} onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}>
                Active (visible in template builder)
              </Checkbox>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => {
                formModal.onClose()
                resetForm()
              }}
            >
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={() => void handleSave()} isLoading={saving}>
              {editingId ? 'Save' : 'Create'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={deleteModal.isOpen} onClose={deleteModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete custom module</ModalHeader>
          <ModalBody>
            <Text>
              Delete <Text as="span" fontFamily="mono">{deleting?.module_key}</Text> ({deleting?.label})? This cannot be
              undone.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={deleteModal.onClose}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={() => void handleDelete()} isLoading={saving}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
